import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts', icon: '📚' },
    { id: 'trends', name: 'Industry Trends', icon: '📈' },
    { id: 'tips', name: 'Marketing Tips', icon: '💡' },
    { id: 'case-studies', name: 'Case Studies', icon: '📊' },
    { id: 'interviews', name: 'KOL Interviews', icon: '🎤' }
  ];

  const blogPosts = [
    {
      id: 1,
      title: 'The Rise of Micro-Influencers in Southeast Asia 2026',
      excerpt: 'Discover why brands are shifting from mega-influencers to micro-influencers for better engagement and ROI in the Malaysian market.',
      category: 'trends',
      author: 'Sarah Chen',
      date: 'March 15, 2026',
      readTime: '8 min read',
      image: '📱'
    },
    {
      id: 2,
      title: 'How to Create the Perfect Brief for Your KOL Campaign',
      excerpt: 'A comprehensive guide to writing influencer marketing briefs that get results. Learn the key elements every brand needs to include.',
      category: 'tips',
      author: 'Ahmad Razif',
      date: 'March 12, 2026',
      readTime: '6 min read',
      image: '📝'
    },
    {
      id: 3,
      title: 'Case Study: How Petronas Achieved 500K Engagements',
      excerpt: 'An in-depth look at how Malaysia\'s national oil company leveraged kol.my to run a successful brand awareness campaign.',
      category: 'case-studies',
      author: 'Wei Jie Tan',
      date: 'March 8, 2026',
      readTime: '10 min read',
      image: '🏆'
    },
    {
      id: 4,
      title: 'Interview: Top Malaysian Food Blogger Shares Her Journey',
      excerpt: 'Exclusive interview with popular food influencer @MalaysianTummy on building a successful personal brand.',
      category: 'interviews',
      author: 'Sarah Chen',
      date: 'March 5, 2026',
      readTime: '12 min read',
      image: '🍜'
    },
    {
      id: 5,
      title: 'Understanding KOL vs Influencer: What\'s the Difference?',
      excerpt: 'Breaking down the key differences between KOLs and influencers, and when to use each for your marketing strategy.',
      category: 'tips',
      author: 'Ahmad Razif',
      date: 'March 1, 2026',
      readTime: '5 min read',
      image: '🤔'
    },
    {
      id: 6,
      title: 'TikTok vs Instagram Reels: Where Should Brands Focus?',
      excerpt: 'Comparing engagement rates, audience demographics, and content strategies across both platforms for Malaysian brands.',
      category: 'trends',
      author: 'Wei Jie Tan',
      date: 'February 25, 2026',
      readTime: '7 min read',
      image: '📱'
    }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-20">
        <div className="mx-auto max-w-6xl text-center">
          <h1 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent md:text-6xl">
            Blog
          </h1>
          <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-400 md:text-2xl">
            Insights, trends, and success stories from the world of influencer marketing in Southeast Asia.
          </p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-violet-600 to-blue-500 text-white shadow-lg'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map(post => (
              <article
                key={post.id}
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-colors"
              >
                <div className="h-48 bg-gradient-to-br from-violet-600/20 to-blue-600/20 flex items-center justify-center">
                  <span className="text-6xl">{post.image}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-violet-500/20 text-violet-400 text-xs font-medium rounded-full">
                      {categories.find(c => c.id === post.category)?.name}
                    </span>
                    <span className="text-sm text-gray-500">{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white hover:text-violet-400 transition">
                    <Link to={`/blog/${post.id}`}>{post.title}</Link>
                  </h3>
                  <p className="text-gray-400 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-blue-500 rounded-full flex items-center justify-center">
                        <span>{post.author[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{post.author}</p>
                        <p className="text-xs text-gray-500">{post.date}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Newsletter Section */}
          <div className="mt-16 bg-gradient-to-r from-violet-600 to-blue-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Get the latest influencer marketing insights, campaign tips, and industry trends 
              delivered straight to your inbox every week.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button
                type="submit"
                className="px-8 py-3 bg-white text-violet-600 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Blog;
